/**
 * The YDM campaign naming convention, checked leniently (spec §9.2): segment 1 a
 * channel code, segment 2 a buy type, and anywhere a target, a date range and a
 * PO/QT reference. Positions after segment 2 vary in real names, so those parts
 * are scanned, not indexed. Vocabularies were measured from local names (2026-10-13).
 * A bare "PO-" segment (number not filled in yet) counts as a reference: owner ruling 2026-10-13.
 *
 * @module services/looker/campaignNaming
 */
export type NamingPart = 'channel' | 'buyType' | 'target' | 'dateRange' | 'reference';
export interface NamingResult { conforms: boolean; missing: NamingPart[] }

export const norm = (s: string): string => s.toLowerCase().replace(/[\s_-]+/g, '');

export const CHANNEL_CODES: readonly string[] = [
  'FB', 'TikTok', 'YT', 'Youtube', 'SEM', 'GSA', 'GDN', 'UAC', 'PMAX', 'Line', 'Demand Gen',
];
export const BUY_TYPES: readonly string[] = [
  'Conversion', 'Conversions', 'Traffic', 'Reach', 'Sales', 'PostEngagement', 'Engage', 'Click',
  'YT In-Stream', 'YT In Stream', 'View', 'Non-skip', 'VideoViews', 'Video view', 'Bumper', 'Install',
  'LeadGen', 'R&F', 'Message', 'Follower', 'Pagelike', 'Subscription',
];
const CHANNELS = new Set(CHANNEL_CODES.map(norm));
const BUYS = new Set(BUY_TYPES.map(norm));

const TAG = /^\s*\[[^\]]*\]\s*/;
const TARGET = /^\s*(cp[a-z]{0,5}|roas|tcpv|imp)\s*-\s*[0-9][0-9.,]*\s*$/i;
const DATE_RANGE = /\b\d{1,2}[A-Za-z]{3}\d{4}\s*-\s*\d{1,2}[A-Za-z]{3}\d{4}\b/;
const REFERENCE = /(PO-\d{8}-\d{4}|QT\s?\d{4}-\d{3}-\d{2})/;
const BLANK_PO = /^PO-$/i;

export const classifyCampaignName = (name: string | null | undefined): NamingResult => {
  const segs = (name ?? '').split('|').map((s) => s.trim());
  const first = (segs[0] ?? '').replace(TAG, '');
  const checks: Array<[NamingPart, boolean]> = [
    ['channel', segs.length > 1 && CHANNELS.has(norm(first))],
    ['buyType', segs.length > 1 && BUYS.has(norm(segs[1] ?? ''))],
    ['target', segs.some((s) => TARGET.test(s))],
    ['dateRange', segs.some((s) => DATE_RANGE.test(s))],
    ['reference', segs.some((s) => REFERENCE.test(s) || BLANK_PO.test(s))],
  ];
  const missing = checks.filter(([, pass]) => !pass).map(([part]) => part);
  return { conforms: missing.length === 0, missing };
};
